import { useCallback, useEffect, useState } from "react";

import { useLazyGetFilterByPropertyQuery } from "@/pages/Private/redux/list/list.api";

import { ReengageOptionItem } from "../schema/reengageSource";

interface UseFilterPropertyOptionsResult {
	options: ReengageOptionItem[];
	isLoading: boolean;
	search: (term: string) => void;
}

/**
 * Loads selectable values for a single curated dropdown via the shared `getFilterByProperty`
 * endpoint, keyed by the filter `propertyName` (e.g. "list", "companyCategory", "country").
 * Maps the `{ value, id }` rows into the `{ id, title }` shape used across the form.
 */
export const useFilterPropertyOptions = (
	propertyName: string,
	listId?: number
): UseFilterPropertyOptionsResult => {
	const [trigger, { isFetching }] = useLazyGetFilterByPropertyQuery();
	const [options, setOptions] = useState<ReengageOptionItem[]>([]);

	const load = useCallback(
		async (term: string) => {
			const result = await trigger({ propertyName, searchTerm: term, listId }).unwrap();

			setOptions(
				(result?.data ?? []).map((row) => ({
					id: row.id?.toString() ?? row.value,
					title: row.value,
				}))
			);
		},
		[trigger, propertyName, listId]
	);

	useEffect(() => {
		void load("");
	}, [load]);

	return { options, isLoading: isFetching, search: (term: string) => void load(term) };
};
